import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import {
  authGrpList,
  authUserList,
  authGrpModify,
  authGrpCreate,
  authGrpDelete,
  authUserMapping,
  groupUserMappingDelete,
} from 'lib/api/auth';
import { showSuccessSnackbar } from 'modules/snackbar';

import CurrentStatus from 'components/atoms/CurrentStatus';
import NewButton from 'components/molecules/NewButton';
import FunctionWrap from 'components/molecules/FunctionWrap';
import NewTableContainer from 'components/molecules/NewTable';

import GroupModal from './GroupModal.js';

const Group = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  let initParams = {
    grpNo: 0,
  };
  let initSubParams = {
    grpNo: 0,
    table: 'user_table',
  };
  const [params, setParams] = useState(initParams);
  const [subParams, setSubParams] = useState(initSubParams);
  const [groupList, setGroupList] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [reFetch, setReFetch] = useState(false);
  const [handleFlag, setHandleFlag] = useState(false);
  const initModal = {
    open: false,
    type: '',
    params: {},
    data: { headerTitle: '' },
  };
  const [modal, setModal] = useState(initModal);

  useEffect(() => {
    if (groupList.length > 0) {
      for (let group of groupList) {
        if (group['GRP_NO'] === params['grpNo']) {
          setGroupName(group['GRP_NAME']);
        }
      }
    }
    // eslint-disable-next-line
  }, [params]);

  const getParam = (newParams) => {
    if (JSON.stringify(params) !== JSON.stringify(newParams)) {
      setParams(newParams);
    }
  };

  const getSubParam = (newParams) => {
    if (JSON.stringify(subParams) !== JSON.stringify(newParams)) {
      setSubParams(newParams);
    }
  };

  const handleRowClick = (newData) => {
    if (
      JSON.stringify(subParams['grpNo']) !== JSON.stringify(newData['GRP_NO'])
    ) {
      const newParams = {
        grpName: newData['GRP_NAME'],
        grpSortNo: newData['GRP_SORT_NO'],
        grpNo: newData['GRP_NO'],
      };
      setModal({
        ...initModal,
        params: newParams,
      });
      setSubParams({
        ...subParams,
        ...newParams,
      });
      setParams({
        ...params,
        ...newParams,
      });
      setHandleFlag(true);
    }
  };

  const handleModalOpen = (e) => {
    const name = e.currentTarget.name;
    let newData = {};
    let newModal = { ...initModal, open: true, type: name, params: params };
    if (name === 'add') {
      newData = {
        headerTitle: t('그룹 추가'),
        confirmBtnTitle: t('추가'),
      };
      newModal = { ...newModal, params: {} };
    } else if (name === 'modify') {
      newData = {
        headerTitle: t('그룹 수정'),
        confirmBtnTitle: t('수정'),
      };
    } else if (name === 'delete') {
      newData = {
        headerTitle: t('그룹 삭제'),
        confirmBtnTitle: t('삭제'),
      };
    } else if (name === 'addUser') {
      newData = {
        headerTitle: t('사용자 추가'),
        confirmBtnTitle: t('추가'),
      };
    }
    newModal = { ...newModal, data: newData };
    setModal(newModal);
  };

  const handleModalGetParams = (newModal) => {
    setModal(newModal);
  };

  const handleModalClose = () => {
    setModal(initModal);
  };

  const handleSubmit = () => {
    if (modal['type'] === 'add') {
      handleReqPromise(authGrpCreate, modal['params']);
    } else if (modal['type'] === 'modify') {
      handleReqPromise(authGrpModify, modal['params']);
    } else if (modal['type'] === 'delete') {
      handleReqPromise(authGrpDelete, params['grpNo']);
    } else if (modal['type'] === 'addUser') {
      handleReqPromise(authUserMapping, {
        grpNos: modal.params.grpNo,
        userIds: modal.params.userIds,
      });
    } else if (modal['type'] === 'userDelete') {
      handleReqPromise(groupUserMappingDelete, modal['params']);
    }
  };

  const handleCheckBtnController = (type, data) => {
    let newModal = { ...initModal };
    let tmpExlNo = [];
    data.forEach(function (rowData) {
      tmpExlNo.push(rowData['USER_ID']);
    });
    newModal = {
      ...newModal,
      open: true,
      type: 'userDelete',
      params: {
        ...newModal.params,
        grpNos: String(params['grpNo']),
        userIds: String(tmpExlNo),
      },
      data: {
        headerTitle: t('유저 권한 삭제'),
        confirmBtnTitle: t('삭제'),
      },
    };
    setModal(newModal);
  };

  const handleReqPromise = (request, parameter) => {
    request(parameter)
      .then((response) => {
        handleClickSnackbar({
          message: response.data.message,
          option: { type: 'success' },
        });
        setReFetch(true);
      })
      .catch((error) => {
        handleClickSnackbar({
          message: error,
          option: { type: 'error' },
        });
      })
      .finally(() => {
        setReFetch(false);
        handleModalClose();
        setModal(initModal);
        setParams(initParams);
        setSubParams(initSubParams);
      });
  };

  const handleClickSnackbar = useCallback((snackbarOptions) => {
    dispatch(showSuccessSnackbar(snackbarOptions));
    //eslint-disable-next-line
  }, []);

  return (
    <>
      <FunctionWrap loading={false}>
        <NewButton
          type="filterButton"
          name="add"
          onClick={handleModalOpen}
          disableRipple
        >
          {t('그룹 추가')}
        </NewButton>
        <NewButton
          type="filterButton"
          name="modify"
          onClick={handleModalOpen}
          disableRipple
          disabled={params['grpNo'] === 0}
        >
          {t('그룹 수정')}
        </NewButton>
        <NewButton
          type="filterButton"
          name="delete"
          onClick={handleModalOpen}
          disableRipple
          disabled={params['grpNo'] === 0}
        >
          {t('그룹 삭제')}
        </NewButton>
        <NewButton
          type="filterButton"
          name="addUser"
          onClick={handleModalOpen}
          disableRipple
          disabled={params['grpNo'] === 0}
        >
          {t('사용자 추가')}
        </NewButton>
      </FunctionWrap>
      <div style={{ display: 'flex' }}>
        <div style={{ width: '49%', marginRight: '2%' }}>
          <NewTableContainer
            dataReqPromise={authGrpList}
            getParam={getParam}
            params={params}
            useTotal={false}
            reFetch={reFetch}
            useExcel={false}
            useFullScreen={false}
            useRowData
            handleRowClick={handleRowClick}
            dimmingLoading={handleFlag}
            pointer
            callBack={(type, data) => {
              if (type === 'success') {
                setGroupList(data);
              }
            }}
            useFetch={false}
            noFetch
            useStoreLoading={false}
          />
        </div>
        <div style={{ width: '49%' }}>
          {params['grpNo'] === 0 ? (
            <CurrentStatus type="noData" />
          ) : (
            <NewTableContainer
              dataReqPromise={authUserList}
              getParam={getSubParam}
              params={subParams}
              useTotal={false}
              reFetch={reFetch}
              useExcel={false}
              useFullScreen={false}
              useFetch={false}
              rowsPerPage={50}
              title={groupName}
              useCheckBox
              hasActController={{ delete: true }}
              headCallBack={handleCheckBtnController}
              callBack={() => {
                setHandleFlag(false);
              }}
              useStoreLoading={false}
            />
          )}
        </div>
      </div>
      {modal['open'] && (
        <GroupModal
          modal={modal}
          handleModalClose={handleModalClose}
          groupName={groupName}
          getModal={handleModalGetParams}
          handleSubmit={handleSubmit}
        />
      )}
    </>
  );
};

export default Group;
